/**
 * useChallengeFilters Hook
 * Handles challenge filtering by tab: active (all), ready (your turn + active), pending, finished.
 * Returns buckets for sectioned list rendering.
 */

import { useState, useMemo } from 'react';
import type { ChallengeWithStats } from '../../../types/challenges';
import type { ChallengeSectionBuckets, FilterType } from '../types/challenges.types';
import { bucketChallengesBySection } from '../helpers/challengeSectionBuckets.helper';

interface UseChallengeFiltersProps {
  challenges: ChallengeWithStats[];
  currentUserId: string;
}

interface UseChallengeFiltersReturn {
  activeFilter: FilterType;
  setActiveFilter: (filter: FilterType) => void;
  filteredChallenges: ChallengeWithStats[];
  sectionBuckets: ChallengeSectionBuckets;
}

export const useChallengeFilters = ({
  challenges,
  currentUserId,
}: UseChallengeFiltersProps): UseChallengeFiltersReturn => {
  const [activeFilter, setActiveFilter] = useState<FilterType>('active');

  const sectionBuckets = useMemo(
    () => bucketChallengesBySection(challenges, currentUserId),
    [challenges, currentUserId]
  );

  const filteredChallenges = useMemo(() => {
    if (activeFilter === 'active') {
      return [
        ...sectionBuckets.yourMove,
        ...sectionBuckets.live,
        ...sectionBuckets.active,
        ...sectionBuckets.waitingForFriends,
        ...sectionBuckets.waitingForTaker,
        ...sectionBuckets.waitingForOpponent,
      ];
    }
    if (activeFilter === 'ready') {
      return [...sectionBuckets.yourMove, ...sectionBuckets.active];
    }
    if (activeFilter === 'pending') {
      return [...sectionBuckets.waitingForFriends, ...sectionBuckets.waitingForTaker];
    }
    if (activeFilter === 'finished') return sectionBuckets.finished;
    return [];
  }, [sectionBuckets, activeFilter]);

  return {
    activeFilter,
    setActiveFilter,
    filteredChallenges,
    sectionBuckets,
  };
};

